import type {
  CartCapabilityDef,
  ChildObjectType,
} from "./integrationDefinitions";

import { z } from "zod";

import {
  contentfulCustomConfigPatchSchema,
  integrationAllowedScopesSchema,
  integrationConnectionIdSchema,
  integrationScopeKeySchema,
  integrationTargetBindingSchema,
} from "../agentIntegration";
import { productStatusSchema } from "../agentProduct";
import { integrationKeySchema, objectTypeKeySchema } from "../chatMessage";
import { integrationTagSchema } from "../integrationTag";
import { mentionPreviewSchema } from "../mentionable";
import { childObjectTypeSchema } from "./integrationDefinitions";

export const createConnectSessionInputSchema = z.object({
  integrationKey: z.string().min(1),
});

export const createConnectSessionOutputSchema = z.object({
  token: z.string(),
  // NOTE (Ryan, 2026-07-31): Headless `nango.auth(providerConfigKey)` needs the
  // Nango provider key. Returned here (auth-gated connect-session endpoint) so
  // it never appears on the public catalog (REPL-30103).
  providerConfigKey: z.string().min(1),
});

export const saveConnectionInputSchema = z.object({
  integrationKey: z.string().min(1),
  connectionScope: integrationScopeKeySchema,
  nangoConnectionId: z.string().min(1),
});

export const saveCustomConnectionInputSchema = z.object({
  integrationKey: z.string().min(1),
  connectionScope: integrationScopeKeySchema,
  customConfig: z.record(z.string(), z.unknown()),
});

export const saveConnectionOutputSchema = z.object({
  connectionId: z.string(),
  // NOTE (Ryan, 2026-07-30): False when credentials were saved but actor tool
  // refresh did not finish cleanly — Connect still succeeds.
  toolsReady: z.boolean(),
});

export const deleteConnectionOutputSchema = z.object({
  success: z.boolean(),
});

export const connectionStatusInputSchema = z.object({
  integrationKey: z.string().min(1),
});

// NOTE (Kevin, 2026-07-29): Who a connection belongs to, so a user can confirm they linked the right one. `subtitle` is a second identifier for that SAME thing (an email, a workspace domain, a handle) — a fact of a different kind belongs in its integration's own meta variant.
export const connectedAccountSchema = z.object({
  name: z.string(),
  avatarUrl: z.string().nullable(),
  subtitle: z.string().nullable(),
});

export type ConnectedAccount = z.infer<typeof connectedAccountSchema>;

// NOTE (Noah, 2026-07-30): One variant per integration rather than a shared
// shape, so a provider's data stays typed instead of being flattened into
// strings to fit. Integrations whose meta really is just an identity reuse
// connectedAccountSchema, and are spelled out individually so any one of them
// can grow its own fields without touching the others.
export const connectionMetaSchema = z.discriminatedUnion("integrationKey", [
  z.object({
    integrationKey: z.literal("contentful"),
    spaces: z.array(
      z.object({
        id: z.string(),
        name: z.string().nullable(),
      }),
    ),
    configuredSpaceIds: z.array(z.string()),
  }),
  z.object({
    // The pinned workspace, so the card shows which store's data the
    // personal API key is scoped to.
    integrationKey: z.literal("converge"),
    workspaceName: z.string().nullable(),
  }),
  z.object({
    integrationKey: z.literal("customer-io"),
    siteId: z.string().nullable(),
    region: z.string().nullable(),
  }),
  z.object({
    integrationKey: z.literal("figma"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("google-analytics"),
    account: connectedAccountSchema.nullable(),
    propertyNames: z.array(z.string()),
  }),
  z.object({
    integrationKey: z.literal("google-calendar"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("google-drive"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    // Search Console exposes no user at all, so the verified sites are the meta.
    integrationKey: z.literal("google-search-console"),
    verifiedSiteUrls: z.array(z.string()),
  }),
  z.object({
    integrationKey: z.literal("gorgias"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("judgeme"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("klaviyo"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("linear"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("loox"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("notion"),
    workspace: connectedAccountSchema.nullable(),
    authorizedByName: z.string().nullable(),
  }),
  z.object({
    integrationKey: z.literal("okendo"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("opus"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("pinterest"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("posthog"),
    account: connectedAccountSchema.nullable(),
    organizationName: z.string().nullable(),
    projectName: z.string().nullable(),
  }),
  z.object({
    integrationKey: z.literal("recharge"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("reviews-io"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("slack"),
    account: connectedAccountSchema.nullable(),
    conversationUrl: z.string().nullish(),
  }),
  z.object({
    integrationKey: z.literal("snapchat-ads"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("stamped"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("stripe"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("triple-whale"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    integrationKey: z.literal("vwo"),
    account: connectedAccountSchema.nullable(),
  }),
  z.object({
    // The app key is an opaque 40-char string, so a null store means the user
    // may have connected the wrong Yotpo app (REPL-29673) — the card says so.
    integrationKey: z.literal("yotpo"),
    store: z.object({ name: z.string(), totalReviews: z.number() }).nullable(),
  }),
]);

export type ConnectionMeta = z.infer<typeof connectionMetaSchema>;

// The latest credential submission the server rejected at one scope of this
// integration. A failed connect stores no connection, so this is the only
// durable record the connect form and the agent have of why that scope is
// still disconnected. User-scoped entries belong to the requesting user only.
export const integrationConnectFailureSchema = z.object({
  connectionScope: integrationScopeKeySchema,
  message: z.string(),
  attemptedAt: z.iso.datetime(),
});

export type IntegrationConnectFailure = z.infer<
  typeof integrationConnectFailureSchema
>;

export const connectionStatusOutputSchema = z.object({
  // At most one entry per scope, only for scopes that are not connected.
  connectFailures: z.array(integrationConnectFailureSchema).optional(),
  connections: z.array(
    z.object({
      connectionId: integrationConnectionIdSchema,
      connectionScope: integrationScopeKeySchema,
      targetBindings: z.array(integrationTargetBindingSchema),
      // True when the stored connection was rejected upstream and the user must
      // reconnect it before the agent can use its tools again (REPL-29041).
      needsReconnect: z.boolean().optional(),
      // Set while a Messages connection waits for the operator to text this
      // code from their phone; the row is unusable until then. The code is
      // null once it expired, so the card offers a new one instead.
      pendingVerification: z
        .object({ code: z.string().nullable(), expiresAt: z.iso.datetime() })
        .optional(),
      // NOTE (Noah, 2026-07-24): Provider metadata resolved only by the single-integration getConnectionStatus (never the list), so the list view pays for no per-provider probe. Absent for integrations that expose nothing to show.
      meta: connectionMetaSchema.optional(),
      // Payment-account state deliberately lives on the payments endpoint
      // (`paymentProviderAccount.getAccount`), never here.
      details: z
        .object({
          shopifyUrl: z.string().optional(),
          primaryDomain: z.string().optional(),
          reportingAccess: z.boolean().optional(),
          reportingAuthorizeUrl: z.string().optional(),
          // Non-secret custom-config field values (keyed by connection field
          // key) safe to surface in the UI, e.g. a custom integration's shop
          // domain. Secret fields are never included.
          connectionConfig: z.record(z.string(), z.string()).optional(),
          // The sender handle a Messages connection is bound to.
          phoneNumber: z.string().optional(),
        })
        .optional(),
    }),
  ),
});

export const allConnectionStatusesOutputSchema = z.record(
  z.string(),
  connectionStatusOutputSchema,
);

export const integrationCatalogItemSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  starterPrompt: z.string().optional(),
  image: z.string().optional(),
  darkImage: z.string().optional(),
  docsUrl: z.string().optional(),
  connectUrl: z
    .string()
    .optional()
    .describe(
      "For an external-mcp-client integration: where the merchant adds Replo in the vendor's own UI.",
    ),
  tags: z.array(integrationTagSchema),
  connectionUi: z.enum([
    "nango-oauth",
    "nango-connect-ui",
    "custom-api-key",
    "shopify-app-install",
    "messages",
    "external-mcp-client",
  ]),
  connectionHighlights: z.array(z.string()).optional(),
  messagesPhoneNumber: z
    .string()
    .optional()
    .describe(
      "For the Messages transport: Replo's phone number customers text to reach the agent.",
    ),
  supportsTargetBindings: z.boolean(),
  // What this provider calls the things access can be restricted to, so the
  // picker can say "ad accounts" instead of "items".
  targetResourceLabel: z
    .object({ singular: z.string(), plural: z.string() })
    .optional(),
  connectionFields: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        type: z.enum(["text", "password"]),
        required: z.boolean(),
        isSecret: z.boolean().optional(),
        helpText: z.string().optional(),
        allowedValues: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  allowedScopes: integrationAllowedScopesSchema,
});

export const integrationCatalogOutputSchema = z.array(
  integrationCatalogItemSchema,
);

export const singleValueFilterOperatorSchema = z.enum([
  "equals",
  "doesNotEqual",
  "lt",
  "lte",
  "gt",
  "gte",
  "contains",
  "doesNotContain",
]);

export const multipleValuesFilterOperatorSchema = z.enum(["in", "notIn"]);

// A discriminated union (rather than a base object intersected with a
// discriminated union) so consumers can narrow on `parity` and TS infers the
// matching `operator`/`value(s)` shape without unwrapping an intersection.
export const filterClauseSchema = z.discriminatedUnion("parity", [
  z.object({
    parity: z.literal("single"),
    dataModelKey: z.string(),
    operator: singleValueFilterOperatorSchema,
    value: z.string(),
  }),
  z.object({
    parity: z.literal("multiple"),
    dataModelKey: z.string(),
    operator: multipleValuesFilterOperatorSchema,
    values: z.array(z.string()),
  }),
]);

export type SingleValueFilterOperator = z.infer<
  typeof singleValueFilterOperatorSchema
>;
export type MultipleValuesFilterOperator = z.infer<
  typeof multipleValuesFilterOperatorSchema
>;

export function singleValueFilter(input: {
  dataModelKey: string;
  operator: SingleValueFilterOperator;
  value: string;
}): FilterClause {
  return { parity: "single", ...input };
}

export function multipleValuesFilter(input: {
  dataModelKey: string;
  operator: MultipleValuesFilterOperator;
  values: string[];
}): FilterClause {
  return { parity: "multiple", ...input };
}

/**
 * Resolves a child object type's `filter` config plus a value pulled from the
 * selected parent into a concrete `FilterClause` for the child search. `in` is
 * the only multiple-value operation; everything else is a single-value filter.
 */
export function buildChildObjectFilterClause(input: {
  filter: ChildObjectType["filter"];
  value: string;
}): FilterClause {
  const { filter, value } = input;
  if (filter.operation === "in") {
    return multipleValuesFilter({
      dataModelKey: filter.dataModelKey,
      operator: "in",
      values: [value],
    });
  }
  return singleValueFilter({
    dataModelKey: filter.dataModelKey,
    operator: filter.operation,
    value,
  });
}

export const searchObjectsInputSchema = z.object({
  integrationKey: z.string().min(1),
  connectionId: integrationConnectionIdSchema.optional(),
  objectType: objectTypeKeySchema,
  query: z.string().optional(),
  limit: z.number().int().positive().max(50).optional(),
  cursor: z.string().optional(),
  filters: z.array(filterClauseSchema).optional(),
});

export const integrationObjectSearchResultSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  handle: z.string().optional(),
  status: productStatusSchema
    .optional()
    .describe("Product lifecycle status when available."),
  imageUrl: z.string().nullish(),
  preview: mentionPreviewSchema.optional(),
  description: z.string().optional(),
  subtitle: z.string().optional(),
  groupLabel: z.string().optional(),
  groupOrder: z.number().optional(),
});

export const integrationObjectSearchPageSchema = z.object({
  results: z.array(integrationObjectSearchResultSchema),
  nextCursor: z.string().nullable(),
});

export const searchObjectsOutputSchema = z.object({
  connectionGroups: z.array(
    integrationObjectSearchPageSchema.extend({
      connectionId: integrationConnectionIdSchema,
      connectionScope: integrationScopeKeySchema,
    }),
  ),
});

export const mentionableObjectSchema = z.object({
  connectionId: integrationConnectionIdSchema,
  connectionScope: integrationScopeKeySchema,
  integrationKey: integrationKeySchema,
  integrationName: z.string(),
  objectType: objectTypeKeySchema,
  displayName: z.string(),
  pluralDisplayName: z.string(),
  tags: z.array(integrationTagSchema),
  browseGroup: z.string(),
  searchable: z.boolean().optional(),
  childObjectTypes: z.array(childObjectTypeSchema).optional(),
  showInMentionMenu: z.boolean().optional(),
});

export const mentionableObjectsOutputSchema = z.array(mentionableObjectSchema);

export const connectedIntegrationSchema = z.object({
  connectionId: z.string().min(1),
  integrationKey: z.string(),
  name: z.string(),
  description: z.string(),
  usageGuidance: z.string(),
  connectionScope: integrationScopeKeySchema,
  scopeContext: z.string(),
  targetBindings: z.array(integrationTargetBindingSchema),
  nangoConnectionId: z.string().nullish(),
  needsReconnect: z.boolean().optional(),
  availableTools: z.array(
    z.object({
      name: z.string(),
      returnsObject: z.unknown().optional(),
    }),
  ),
  availableLoaders: z
    .array(
      z.object({
        loaderKey: z.string(),
        description: z.string(),
        scaffoldComponentName: z.string(),
        scaffoldImportPath: z.string(),
        returnsObject: z.unknown().optional(),
      }),
    )
    .optional(),
  availableObjects: z
    .array(
      z.object({
        objectTypeKey: objectTypeKeySchema,
        tags: z.array(integrationTagSchema),
        searchToolKey: z.string().optional(),
        suggestedLoaderKeys: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  availableCart: z.custom<CartCapabilityDef>().optional(),
});

export const integrationToolDescriptorSchema = z.object({
  integrationKey: z.string(),
  toolName: z.string(),
  localToolName: z.string(),
  title: z.string(),
  description: z.string(),
  inputJsonSchema: z.record(z.string(), z.unknown()),
  outputJsonSchema: z.record(z.string(), z.unknown()).optional(),
  annotations: z.record(z.string(), z.unknown()).optional(),
});

export const executeToolInputSchema = z.object({
  connectionId: integrationConnectionIdSchema.optional(),
  toolName: z.string().min(1),
  args: z.record(z.string(), z.unknown()),
});

export const discoverIntegrationTargetsOutputSchema = z.object({
  targets: z.array(integrationTargetBindingSchema),
});

const updateConnectionTargetBindingsSchema = z.array(
  integrationTargetBindingSchema,
);

const updateConnectionContentfulSchema = z.object({
  integrationKey: z.literal("contentful"),
  targetBindings: updateConnectionTargetBindingsSchema.optional(),
  customConfig: contentfulCustomConfigPatchSchema.optional(),
});

const updateConnectionBindingsOnlySchema = z.object({
  integrationKey: z.enum([
    "google-ads",
    "google-analytics",
    "google-search-console",
    "meta-marketing-api",
    "posthog",
  ]),
  targetBindings: updateConnectionTargetBindingsSchema,
});

export const updateConnectionInputSchema = z.discriminatedUnion(
  "integrationKey",
  [updateConnectionContentfulSchema, updateConnectionBindingsOnlySchema],
);

export const executeToolContentItemSchema = z.object({
  type: z.string(),
  text: z.string().optional(),
});

export const executeToolOutputSchema = z.object({
  success: z.boolean(),
  content: z
    .union([z.string(), z.array(executeToolContentItemSchema)])
    .optional(),
  error: z.string().optional(),
});

export type IntegrationCatalogItem = z.infer<
  typeof integrationCatalogItemSchema
>;
export type IntegrationToolDescriptor = z.infer<
  typeof integrationToolDescriptorSchema
>;
export type ConnectedIntegration = z.infer<typeof connectedIntegrationSchema>;
export type IntegrationObjectSearchResult = z.infer<
  typeof integrationObjectSearchResultSchema
>;
export type IntegrationObjectSearchPage = z.infer<
  typeof integrationObjectSearchPageSchema
>;
export type MentionableObject = z.infer<typeof mentionableObjectSchema>;
export type FilterClause = z.infer<typeof filterClauseSchema>;
